import React, { useState, useEffect } from 'react';
import {
  Workflow, Plus, Edit2, Trash2, X, Users, Play, Pause, Check, Clock,
  ChevronRight, Send, AlertCircle, ArrowUp, ArrowDown,
} from 'lucide-react';
import {
  Badge, emptyMsg, cardWrap, neuShadow, neuShadowSm,
  btnPrimary, btnSm, inp,
  overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn,
} from './crmShared';

/* ═══════════════════════════════════════════════════════════════════════════
   SequencesTab — manage drip campaigns (multi-step SMS / email sequences).
   • List sequences w/ enrollment counts
   • Create / edit / delete sequences
   • Add / remove / reorder steps with delay + body editor
   • Enroll contacts via filter (reuses bulk-send filter shape)
   • View enrollments + manually exit
   ═══════════════════════════════════════════════════════════════════════════ */

export default function SequencesTab({ canCreate }) {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSeq, setEditingSeq] = useState(null);  // null = create, object = edit
  const [enrollSeq, setEnrollSeq] = useState(null);
  const [viewEnrollments, setViewEnrollments] = useState(null);
  const [showModal, setShowModal] = useState(null);   // 'create' | 'edit' | 'enroll' | 'enrollments' | null

  async function load() {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.listSequences();
      setSequences(Array.isArray(r) ? r : []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function toggleActive(seq) {
    const full = await window.zignagenixAPI.getSequence(seq.id);
    await window.zignagenixAPI.updateSequence(seq.id, {
      name: full.name,
      description: full.description,
      defaultChannel: full.default_channel,
      isActive: !full.is_active,
      // No steps key → server preserves existing steps untouched.
    });
    load();
  }

  async function deleteSeq(seq) {
    if (!window.confirm(`Delete sequence "${seq.name}" and all its enrollments? This cannot be undone.`)) return;
    await window.zignagenixAPI.deleteSequence(seq.id);
    load();
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Workflow size={16} color="#2E8B98" />
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#212529' }}>Sequences</h2>
          </div>
          <div style={{ fontSize: 12, color: '#6C757D', maxWidth: 640 }}>
            Multi-step drip campaigns. Each step waits a configurable delay, then sends an SMS or email.
            Contacts auto-exit when they reply (configurable per step). Enrolled contacts move through
            steps automatically — the processor runs every 15 minutes.
          </div>
        </div>
        {canCreate && (
          <button onClick={() => { setEditingSeq(null); setShowModal('create'); }} style={btnPrimary}>
            <Plus size={14} /> New Sequence
          </button>
        )}
      </div>

      {loading ? (
        <div style={emptyMsg}>Loading sequences…</div>
      ) : sequences.length === 0 ? (
        <div style={{ ...cardWrap, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#6C757D', marginBottom: 8 }}>
            No sequences yet.
          </div>
          <div style={{ fontSize: 11, color: '#ADB5BD', marginBottom: 14 }}>
            Build a "5-day reactivation" sequence for cold leads, a "post-consult follow-up" with three
            touchpoints, or any other multi-step campaign.
          </div>
          {canCreate && (
            <button onClick={() => { setEditingSeq(null); setShowModal('create'); }} style={btnPrimary}>
              <Plus size={14} /> Create your first sequence
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sequences.map(s => (
            <SequenceRow
              key={s.id}
              seq={s}
              canCreate={canCreate}
              onEdit={async () => {
                const full = await window.zignagenixAPI.getSequence(s.id);
                setEditingSeq(full);
                setShowModal('edit');
              }}
              onEnroll={() => { setEnrollSeq(s); setShowModal('enroll'); }}
              onViewEnrollments={() => { setViewEnrollments(s); setShowModal('enrollments'); }}
              onToggleActive={() => toggleActive(s)}
              onDelete={() => deleteSeq(s)}
            />
          ))}
        </div>
      )}

      {showModal === 'create' && (
        <SequenceEditModal mode="create" onClose={() => setShowModal(null)} onSaved={() => { setShowModal(null); load(); }} />
      )}
      {showModal === 'edit' && editingSeq && (
        <SequenceEditModal mode="edit" seq={editingSeq} onClose={() => setShowModal(null)} onSaved={() => { setShowModal(null); load(); }} />
      )}
      {showModal === 'enroll' && enrollSeq && (
        <EnrollModal seq={enrollSeq} onClose={() => setShowModal(null)} onEnrolled={() => { setShowModal(null); load(); }} />
      )}
      {showModal === 'enrollments' && viewEnrollments && (
        <EnrollmentsModal seq={viewEnrollments} onClose={() => setShowModal(null)} onChanged={() => load()} />
      )}
    </>
  );
}

/* ─── Sequence row ─────────────────────────────────────────────────────────── */

function SequenceRow({ seq, canCreate, onEdit, onEnroll, onViewEnrollments, onToggleActive, onDelete }) {
  return (
    <div style={{ ...cardWrap, padding: 14, opacity: seq.is_active ? 1 : 0.6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 6,
          background: seq.is_active ? '#DCFCE7' : '#F1F5F9',
          color: seq.is_active ? '#15803D' : '#64748B',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {seq.is_active ? <Play size={14} /> : <Pause size={14} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>{seq.name}</div>
          {seq.description && <div style={{ fontSize: 11, color: '#6C757D' }}>{seq.description}</div>}
        </div>
        <Badge text={`${seq.step_count || 0} step${seq.step_count === 1 ? '' : 's'}`} color="#2E8B98" />
        <Badge text={seq.default_channel.toUpperCase()} color={seq.default_channel === 'sms' ? '#62BB46' : '#2E8B98'} />
        {!seq.is_active && <Badge text="Disabled" color="#94A3B8" />}
      </div>
      <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#6C757D', alignItems: 'center', marginTop: 6 }}>
        <span><strong style={{ color: '#15803D' }}>{seq.active_enrolled || 0}</strong> active</span>
        <span>·</span>
        <span><strong style={{ color: '#475569' }}>{seq.completed_count || 0}</strong> completed</span>
        <span>·</span>
        <span><strong style={{ color: '#94A3B8' }}>{seq.exited_count || 0}</strong> exited</span>
        <div style={{ flex: 1 }} />
        {canCreate && (
          <>
            <button onClick={onEnroll} style={btnSm} title="Enroll contacts"><Users size={12} /> Enroll</button>
            <button onClick={onViewEnrollments} style={btnSm} title="View enrollments"><ChevronRight size={12} /> Enrollments</button>
            <button onClick={onToggleActive} style={btnSm} title={seq.is_active ? 'Disable' : 'Enable'}>
              <Check size={12} color={seq.is_active ? '#15803D' : '#94A3B8'} />
            </button>
            <button onClick={onEdit} style={btnSm} title="Edit"><Edit2 size={12} color="#2E8B98" /></button>
            <button onClick={onDelete} style={btnSm} title="Delete"><Trash2 size={12} color="#DC3545" /></button>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Create / edit modal ──────────────────────────────────────────────────── */

function SequenceEditModal({ mode, seq, onClose, onSaved }) {
  const [name, setName] = useState(seq?.name || '');
  const [description, setDescription] = useState(seq?.description || '');
  const [defaultChannel, setDefaultChannel] = useState(seq?.default_channel || 'sms');
  const [isActive, setIsActive] = useState(seq ? seq.is_active : true);
  const [steps, setSteps] = useState(() =>
    (seq?.steps || []).map(s => ({
      delayDays: s.delay_days,
      delayHours: s.delay_hours,
      channel: s.channel || '',
      subject: s.subject || '',
      body: s.body || '',
      exitOnReply: s.exit_on_reply !== false,
    }))
  );
  const [saving, setSaving] = useState(false);

  function addStep() {
    setSteps(s => [...s, { delayDays: steps.length === 0 ? 0 : 3, delayHours: 0, channel: '', subject: '', body: '', exitOnReply: true }]);
  }
  function removeStep(i) { setSteps(s => s.filter((_, idx) => idx !== i)); }
  function moveStep(i, dir) {
    setSteps(s => {
      const arr = [...s];
      const swap = i + dir;
      if (swap < 0 || swap >= arr.length) return s;
      [arr[i], arr[swap]] = [arr[swap], arr[i]];
      return arr;
    });
  }
  function upStep(i, k, v) {
    setSteps(s => s.map((st, idx) => idx === i ? { ...st, [k]: v } : st));
  }

  async function save(e) {
    e.preventDefault();
    if (!name.trim()) { alert('Name is required'); return; }
    if (steps.length === 0) { alert('Add at least one step'); return; }
    if (steps.some(s => !s.body.trim())) { alert('Every step needs a body'); return; }
    setSaving(true);
    try {
      const payload = {
        name, description: description || null, defaultChannel, isActive,
        steps: steps.map(s => ({
          delayDays: parseInt(s.delayDays, 10) || 0,
          delayHours: parseInt(s.delayHours, 10) || 0,
          channel: s.channel || null,
          subject: s.subject || null,
          body: s.body,
          exitOnReply: !!s.exitOnReply,
        })),
      };
      if (mode === 'create') await window.zignagenixAPI.createSequence(payload);
      else await window.zignagenixAPI.updateSequence(seq.id, payload);
      onSaved();
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 720 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>{mode === 'create' ? 'New Sequence' : `Edit: ${seq?.name}`}</h2>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>
        <form onSubmit={save}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><Lbl>Name *</Lbl><input value={name} onChange={e => setName(e.target.value)} required style={inp} placeholder="e.g. Cold lead reactivation" /></div>
            <div><Lbl>Default channel</Lbl>
              <select value={defaultChannel} onChange={e => setDefaultChannel(e.target.value)} style={inp}>
                <option value="sms">SMS</option>
                <option value="email">Email</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Lbl>Description (optional)</Lbl>
            <input value={description} onChange={e => setDescription(e.target.value)} style={inp} placeholder="Internal note about when to use this sequence" />
          </div>

          <div style={{ ...cardWrap, padding: 12, marginBottom: 14, background: '#FAFBFC' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Steps</span>
              <button type="button" onClick={addStep} style={btnSm}>
                <Plus size={12} /> Add step
              </button>
            </div>
            {steps.length === 0 ? (
              <div style={{ ...emptyMsg, padding: 16 }}>No steps yet. Add at least one.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {steps.map((s, i) => (
                  <div key={i} style={{ ...cardWrap, padding: 10, background: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <Badge text={`Step ${i + 1}`} color="#2E8B98" />
                      <Clock size={11} color="#6C757D" />
                      <span style={{ fontSize: 11, color: '#6C757D' }}>
                        Wait {s.delayDays}d {s.delayHours}h after {i === 0 ? 'enrollment' : `step ${i}`}
                      </span>
                      <div style={{ flex: 1 }} />
                      <button type="button" onClick={() => moveStep(i, -1)} disabled={i === 0} style={iconBtn}>
                        <ArrowUp size={11} color={i === 0 ? '#CBD5E1' : '#475569'} />
                      </button>
                      <button type="button" onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1} style={iconBtn}>
                        <ArrowDown size={11} color={i === steps.length - 1 ? '#CBD5E1' : '#475569'} />
                      </button>
                      <button type="button" onClick={() => removeStep(i)} style={iconBtn} title="Remove step">
                        <X size={11} color="#DC3545" />
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 6 }}>
                      <div><Lbl>Delay days</Lbl><input type="number" min="0" value={s.delayDays} onChange={e => upStep(i, 'delayDays', e.target.value)} style={inp} /></div>
                      <div><Lbl>Delay hours</Lbl><input type="number" min="0" max="23" value={s.delayHours} onChange={e => upStep(i, 'delayHours', e.target.value)} style={inp} /></div>
                      <div><Lbl>Channel override</Lbl>
                        <select value={s.channel} onChange={e => upStep(i, 'channel', e.target.value)} style={inp}>
                          <option value="">Use default ({defaultChannel.toUpperCase()})</option>
                          <option value="sms">SMS</option>
                          <option value="email">Email</option>
                        </select>
                      </div>
                    </div>
                    {(s.channel === 'email' || (!s.channel && defaultChannel === 'email')) && (
                      <div style={{ marginBottom: 6 }}>
                        <Lbl>Subject</Lbl>
                        <input value={s.subject} onChange={e => upStep(i, 'subject', e.target.value)} style={inp} />
                      </div>
                    )}
                    <div style={{ marginBottom: 6 }}>
                      <Lbl>Body — supports {`{{first_name}} {{last_name}} {{organization}}`}</Lbl>
                      <textarea rows={3} value={s.body} onChange={e => upStep(i, 'body', e.target.value)} style={{ ...inp, resize: 'vertical' }} required />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#475569' }}>
                      <input type="checkbox" checked={s.exitOnReply} onChange={e => upStep(i, 'exitOnReply', e.target.checked)} />
                      Exit sequence if contact replies before this step
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, cursor: 'pointer', fontSize: 12 }}>
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
            Active (disabled sequences pause without exiting active enrollments)
          </label>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSm}>Cancel</button>
            <button type="submit" disabled={saving} style={btnPrimary}>
              {saving ? 'Saving…' : (mode === 'create' ? 'Create Sequence' : 'Save Changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Enroll modal ─────────────────────────────────────────────────────────── */

function EnrollModal({ seq, onClose, onEnrolled }) {
  const [filter, setFilter] = useState({ contactType: '', pipelineStage: '', leadSource: '', tagContains: '' });
  const [staggerSeconds, setStaggerSeconds] = useState(0);
  const [enrolling, setEnrolling] = useState(false);
  const [result, setResult] = useState(null);

  async function enroll() {
    setEnrolling(true);
    try {
      const r = await window.zignagenixAPI.enrollInSequence({
        sequenceId: seq.id,
        filter,
        staggerSeconds,
      });
      setResult(r);
      if (r.success) {
        setTimeout(() => { onEnrolled?.(); }, 2000);
      }
    } catch (err) {
      setResult({ success: false, error: err.message });
    } finally {
      setEnrolling(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 540 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>Enroll in: {seq.name}</h2>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>
        <div style={{ ...cardWrap, padding: 12, marginBottom: 14, background: '#FAFBFC' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', marginBottom: 8 }}>
            Filter recipients (all optional, AND'd together)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <Lbl>Contact type</Lbl>
              <select value={filter.contactType} onChange={e => setFilter(f => ({ ...f, contactType: e.target.value }))} style={inp}>
                <option value="">Any</option>
                <option value="prospect">Prospect</option>
                <option value="referral_physician">Referral physician</option>
                <option value="vendor">Vendor</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Lbl>Pipeline stage</Lbl>
              <input value={filter.pipelineStage} onChange={e => setFilter(f => ({ ...f, pipelineStage: e.target.value }))} placeholder="leave blank = any" style={inp} />
            </div>
            <div>
              <Lbl>Lead source contains</Lbl>
              <input value={filter.leadSource} onChange={e => setFilter(f => ({ ...f, leadSource: e.target.value }))} placeholder="e.g. facebook" style={inp} />
            </div>
            <div>
              <Lbl>Tag contains</Lbl>
              <input value={filter.tagContains} onChange={e => setFilter(f => ({ ...f, tagContains: e.target.value }))} placeholder="e.g. VIP" style={inp} />
            </div>
            <div>
              <Lbl>Stagger (sec between enrollments)</Lbl>
              <input type="number" min="0" max="60" value={staggerSeconds} onChange={e => setStaggerSeconds(parseInt(e.target.value, 10) || 0)} style={inp} />
            </div>
          </div>
        </div>
        <div style={{
          padding: '8px 12px', borderRadius: 8, marginBottom: 12,
          background: '#FEF3C7', display: 'flex', alignItems: 'flex-start', gap: 8,
          fontSize: 11, color: '#7C2D12',
        }}>
          <AlertCircle size={14} color="#B45309" style={{ marginTop: 1, flexShrink: 0 }} />
          <div>Already-active enrollments are skipped automatically. The first step fires after its
            configured delay; the sequence processor advances each enrollment every 15 minutes.</div>
        </div>
        {result && (
          <div style={{
            padding: '10px 14px', borderRadius: 10, marginBottom: 12,
            background: result.success ? '#dcfce7' : '#fef2f2',
            color: result.success ? '#166534' : '#991b1b', fontSize: 12,
          }}>
            {result.success
              ? <span>✓ Enrolled <strong>{result.enrolled}</strong> contacts ({result.skipped} skipped — already active)</span>
              : <span>✗ {result.error}</span>}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={btnSm}>Cancel</button>
          <button type="button" onClick={enroll} disabled={enrolling} style={btnPrimary}>
            <Send size={13} /> {enrolling ? 'Enrolling…' : 'Enroll matching contacts'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Enrollments list modal ───────────────────────────────────────────────── */

function EnrollmentsModal({ seq, onClose, onChanged }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');

  async function load() {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.listSequenceEnrollments({
        sequenceId: seq.id, status: statusFilter || null,
      });
      setEnrollments(Array.isArray(r) ? r : []);
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line
  useEffect(() => { load(); }, [statusFilter]);

  async function exitOne(e) {
    if (!window.confirm(`Exit ${e.first_name} ${e.last_name} from this sequence?`)) return;
    await window.zignagenixAPI.exitSequenceEnrollment(e.id, 'manual');
    load(); onChanged?.();
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 720 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>Enrollments — {seq.name}</h2>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {['active', 'completed', 'exited', ''].map(s => (
            <button key={s || 'all'} onClick={() => setStatusFilter(s)} style={{
              padding: '5px 10px', borderRadius: 999, border: 'none',
              background: statusFilter === s ? '#2E8B98' : '#F1F5F9',
              color: statusFilter === s ? '#fff' : '#475569',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}>{s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}</button>
          ))}
        </div>
        {loading ? (
          <div style={emptyMsg}>Loading…</div>
        ) : enrollments.length === 0 ? (
          <div style={emptyMsg}>No enrollments matching filter.</div>
        ) : (
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {enrollments.map(e => (
              <div key={e.id} style={{
                display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 80px',
                padding: '8px 10px', borderBottom: '1px solid #F3F4F6',
                alignItems: 'center', fontSize: 12, gap: 8,
              }}>
                <div style={{ fontWeight: 600 }}>{e.first_name} {e.last_name}</div>
                <div>
                  <Badge text={e.status} color={
                    e.status === 'active' ? '#15803D' : e.status === 'exited' ? '#94A3B8' : '#475569'
                  } />
                </div>
                <div style={{ fontSize: 11, color: '#6C757D' }}>Step {e.current_step}</div>
                <div style={{ fontSize: 10, color: '#6C757D' }}>
                  {e.exit_reason ? `Exit: ${e.exit_reason}` :
                   e.next_step_due_at ? `Due ${new Date(e.next_step_due_at).toLocaleString()}` : '—'}
                </div>
                <div>
                  {e.status === 'active' && (
                    <button onClick={() => exitOne(e)} style={btnSm} title="Exit enrollment">
                      <X size={11} color="#DC3545" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

const Lbl = ({ children }) => (
  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>
    {children}
  </label>
);

const iconBtn = {
  background: '#F8F9FA', border: 'none', cursor: 'pointer',
  padding: 4, borderRadius: 6, boxShadow: neuShadowSm,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
